import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  PanelLeftClose,
  PanelLeft,
  ClipboardList,
  Wand2,
  ShoppingCart,
  BarChart3,
  LayoutDashboard,
} from 'lucide-react'
import { CampaignFormModal } from '../components/CampaignFormModal'
import { Header } from '../components/shared/Header'
import { CampaignData, ChatMessage } from '../types'
import { AccumulatedInputs } from '../types/streaming'
import { CampaignProposalTab } from '../components/tabs/CampaignProposalTab'
import { CreativeGenerationTab } from '../components/tabs/CreativeGenerationTab'
import { CreativeStudioTab } from '../components/tabs/CreativeStudioTab'
import { AICreativeModal, AICreativePayload } from '../components/creative-studio/AICreativeModal'
import { CreateAdUnitsAssetsModal } from '../components/campaignproposal/CreateAdUnitsAssetsModal'
import { MediaBuyTab } from '../components/tabs/MediaBuyTab'
import { ReportInsightsTab } from '../components/tabs/ReportInsightsTab'
import { ChatPanel } from '../components/chat/ChatPanel'
import {
  saveInputsToStorage,
  getVisibleTabs
} from '../lib/apiService'
import { conversationApi, SelectedTemplate } from '../lib/api/conversations'
import { campaignApi } from '../lib/api/campaigns'
import { adUnitsApi } from '../lib/api/adUnits'
import { useChat } from '../hooks/useChat'
import { 
  ProcessingAdUnit,
  CreatedAdUnit,
  PreviewCreativeStatusData,
  PreviewCreativeCreatedData,
  MediaBuyCompleteData,
  MediaBuyData,
  CreativeSyncCompletedData,
  CreativeSyncResult,
  AiCreativeCreationCompletedData,
  isDisplayableMessage,
  isUserMessage,
  isCampaignAgentMessage,
  formatCampaignAgentMessage,
  isWorkflowComplete,
  shouldShowThinking,
} from '../lib/actionCable'

/**
 * Decide whether a conversation's `response_state` actually represents an
 * orchestrator run that's mid-flight. The orchestrator-running flag drives
 * the proposal-tab skeleton ("Generating Media Planner…") and the tab
 * visibility allowlist, so being precise here matters.
 *
 * Pitfalls this guards against:
 *   - Manually-created campaigns: conversation may exist but no workflow
 *     was ever invoked. `response_state` is `undefined` / `null` / `{}`,
 *     yet legacy code treated "not at end" as "still running" and showed
 *     a permanent loading skeleton on the Campaign Proposal tab.
 *   - Conversations that finished but only persisted a partial state (no
 *     `next_node`). We don't want to flip them back into a running state
 *     either — without `next_node` there's nothing for the workflow to
 *     resume, so the user is just looking at the final snapshot.
 *
 * We treat the workflow as active only when there's both a `next_node`
 * set *and* it isn't the terminal `end` value. Any other state — missing,
 * empty object, or `next_node === 'end'` — counts as "not running".
 */
function hasActiveOrchestratorSignal(responseState: unknown): boolean {
  if (!responseState || typeof responseState !== 'object') return false
  const rs = responseState as Record<string, unknown>
  if (Object.keys(rs).length === 0) return false
  const nextNode = typeof rs.next_node === 'string' ? rs.next_node : undefined
  if (!nextNode) return false
  return nextNode !== 'end'
}

export default function Campaigns() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: campaignId, adUnitId: adUnitIdParam } = useParams<{ id: string; adUnitId?: string }>()
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null)
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true)
  const activeAdUnitId = adUnitIdParam ? parseInt(adUnitIdParam, 10) : null
  // Whether the user landed here with an explicit hash (`#campaignproposal`,
  // `#creativegeneration`, etc.). If they did, we honour it verbatim.
  // If they didn't (bare `/campaigns/:id` — e.g. a click on a card in
  // the /campaigns list), we tentatively start on 'proposal' but the
  // "default tab resolver" effect below may swap us onto 'creative'
  // once the fetched conversation reveals no proposal-shaped data.
  const initialHashRef = useRef<string>(
    typeof window !== 'undefined' ? window.location.hash.replace('#', '').toLowerCase() : '',
  )
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (adUnitIdParam) return 'creativeStudio'
    const hash = initialHashRef.current
    const tabIdMap: Record<string, string> = {
      campaignproposal: 'proposal',
      creativegeneration: 'creative',
      mediabuy: 'mediaBuy',
      insights: 'insights'
    }
    return tabIdMap[hash] || 'proposal'
  })
  // Tracks whether we've already resolved the initial default tab
  // from live data. Runs once — after that the user's clicks own
  // `activeTab`. Without this guard, every `tabVisibility` change
  // (e.g. proposal data streaming in later) would yank the user
  // back onto whichever tab we think is the "right" default.
  const defaultTabResolvedRef = useRef(false)
  // Track selected templates and sizes (used when creating ad units)
  const [, setSelectedTemplateIds] = useState<string[]>([])
  const [, setSelectedSizes] = useState<Map<string, Set<string>>>(new Map())
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [advertiserId, setAdvertiserId] = useState<number | null>(null)
  const [processingAdUnits, setProcessingAdUnits] = useState<ProcessingAdUnit[]>([])
  const [completedAdUnits, setCompletedAdUnits] = useState<CreatedAdUnit[]>([])
  const [isCreatingAdUnits, setIsCreatingAdUnits] = useState(false)
  // Mirror in a ref so callbacks captured by useChat (which subscribes once
  // per conversationId) can read the latest value without re-subscribing.
  const isCreatingAdUnitsRef = useRef(false)
  useEffect(() => { isCreatingAdUnitsRef.current = isCreatingAdUnits }, [isCreatingAdUnits])
  // Track which ad_unit_id we've already auto-navigated to so a re-render
  // (or a second response_state snapshot) doesn't trigger a duplicate
  // navigate(). Reset is implicit — a new generation produces a new id.
  const aiCreativeNavigatedAdUnitIdRef = useRef<number | null>(null)
  const [isCreatingMediaBuy, setIsCreatingMediaBuy] = useState(false)
  const [mediaBuyData, setMediaBuyData] = useState<MediaBuyData | null>(null)
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])
  const [syncedCreatives, setSyncedCreatives] = useState<CreativeSyncResult[]>([])
  const [hasApiAdUnits, setHasApiAdUnits] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentFormData, setCurrentFormData] = useState<any>(null)
  const [currentChatInput, setCurrentChatInput] = useState('')
  // Creative Studio / AI Creative state
  const [studioSelectedSizes, setStudioSelectedSizes] = useState<string[]>([])
  const [showAICreativeModal, setShowAICreativeModal] = useState(false)
  const [isAssetsModalOpen, setIsAssetsModalOpen] = useState(false)
  const [pendingCreateAdUnits, setPendingCreateAdUnits] = useState<SelectedTemplate[] | null>(null)
  const [isChatPanelCollapsed, setIsChatPanelCollapsed] = useState(false)

  // Chat panel resize — draggable right edge. Persisted to localStorage so the
  // user's preferred width carries across reloads.
  const CHAT_PANEL_MIN_WIDTH = 280
  const CHAT_PANEL_MAX_WIDTH = 720
  const CHAT_PANEL_DEFAULT_WIDTH = 320
  const CHAT_PANEL_STORAGE_KEY = 'campaigns:chatPanelWidth'
  const [chatPanelWidth, setChatPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return CHAT_PANEL_DEFAULT_WIDTH
    const stored = window.localStorage.getItem(CHAT_PANEL_STORAGE_KEY)
    const parsed = stored ? parseInt(stored, 10) : NaN
    if (!Number.isFinite(parsed)) return CHAT_PANEL_DEFAULT_WIDTH
    return Math.min(CHAT_PANEL_MAX_WIDTH, Math.max(CHAT_PANEL_MIN_WIDTH, parsed))
  })
  const [isResizingChat, setIsResizingChat] = useState(false)
  const chatPanelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAT_PANEL_STORAGE_KEY, String(chatPanelWidth))
    } catch { /* localStorage may be unavailable in private mode */ }
  }, [chatPanelWidth])

  const handleChatPanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    if (isChatPanelCollapsed) return
    const panel = chatPanelRef.current
    if (!panel) return
    const startLeft = panel.getBoundingClientRect().left
    setIsResizingChat(true)

    const onMove = (ev: MouseEvent) => {
      const next = ev.clientX - startLeft
      const clamped = Math.min(
        CHAT_PANEL_MAX_WIDTH,
        Math.max(CHAT_PANEL_MIN_WIDTH, next),
      )
      setChatPanelWidth(clamped)
    }
    const onUp = () => {
      setIsResizingChat(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [isChatPanelCollapsed])

  const handleChatPanelResizeDoubleClick = useCallback(() => {
    setChatPanelWidth(CHAT_PANEL_DEFAULT_WIDTH)
  }, [])

  // While dragging: lock the cursor + disable text selection globally so the
  // gesture feels native even if the pointer briefly leaves the handle.
  useEffect(() => {
    if (!isResizingChat) return
    const prevCursor = document.body.style.cursor
    const prevSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevSelect
    }
  }, [isResizingChat])
  // Start empty and let the effects below seed this from navigation
  // state OR the freshly-fetched conversation. The global localStorage
  // bucket is shared across every campaign, so initialising from it here
  // would flash stale tab visibility (mostly the "Campaign Proposal"
  // tab) for the first paint of an unrelated campaign — exactly the
  // cross-conversation menu leak the user reported.
  const [accumulatedInputs, setAccumulatedInputs] = useState<AccumulatedInputs>(() => ({} as AccumulatedInputs))
  
  const [activeSubTab, setActiveSubTab] = useState('media') // For Campaign Proposal sub-tabs
  const [isOrchestratorRunning, setIsOrchestratorRunning] = useState(false)
  
  // Tab ID <-> URL hash mapping
  const TAB_HASH_MAP: Record<string, string> = {
    proposal: 'campaignproposal',
    creative: 'creativegeneration',
    mediaBuy: 'mediabuy',
    insights: 'insights'
  }
  // All tab definitions. Icons are Lucide components rendered inline so the
  // shell matches the design language used by every other tab/sub-tab nav.
  const allTabs = [
    { id: 'proposal', label: 'Campaign Proposal', icon: ClipboardList },
    { id: 'creative', label: 'Creative Generation', icon: Wand2 },
    { id: 'mediaBuy', label: 'Media Buy', icon: ShoppingCart },
    { id: 'insights', label: 'Insights', icon: BarChart3 },
  ]
  
  // Dynamic tab visibility based on available data
  const tabVisibility = useMemo(() => getVisibleTabs(accumulatedInputs, isOrchestratorRunning), [accumulatedInputs, isOrchestratorRunning])
  
  // Check if we have any ad units (processing, completed, or from API)
  const hasAdUnits = processingAdUnits.length > 0 || completedAdUnits.length > 0 || hasApiAdUnits
  
  // Check if we have sales recommendations for Media Buy tab
  const hasSalesRecommendations = Boolean(campaignData?.salesRecommendations?.data?.products && campaignData.salesRecommendations.data.products.length > 0)
  
  // Filter tabs based on available data
  const tabs = useMemo(() => {
    return allTabs.filter(tab => {
      if (tab.id === 'proposal') return tabVisibility.proposal
      if (tab.id === 'creative') return true
      if (tab.id === 'mediaBuy') return true
      if (tab.id === 'insights') return tabVisibility.insights
      return true
    })
  }, [tabVisibility, hasAdUnits, hasSalesRecommendations])

  // Resolve the initial default tab from live data — once per mount.
  //
  // Why this exists: the /campaigns list card just navigates to
  // `/campaigns/:id` with no hash and no `apiResponse.inputs` in
  // navigation state, so at first paint we optimistically default to
  // `'proposal'`. If the freshly-fetched conversation has no
  // proposal-shaped data, that tab is filtered out of the sidebar and
  // the page shows an empty content area for whichever tab renders
  // first. Once the fetch settles and `tabVisibility` is authoritative,
  // fall through to `'creative'` (always visible) when proposal is
  // hidden. Same rule the "View Campaign" button on Home uses.
  //
  // Runs only when the user did NOT explicitly deep-link into a tab
  // (no hash on entry) and NOT when they're in Creative Studio mode
  // (ad-unit route owns the tab). The `defaultTabResolvedRef` guard
  // makes this a one-shot so user clicks can't be overridden later
  // when proposal data streams in mid-session.
  useEffect(() => {
    if (defaultTabResolvedRef.current) return
    if (adUnitIdParam) return
    if (isLoadingCampaign) return
    if (initialHashRef.current) {
      // User arrived with an explicit hash — respect it verbatim,
      // just mark resolved so we don't re-enter.
      defaultTabResolvedRef.current = true
      return
    }
    if (activeTab === 'proposal' && !tabVisibility.proposal) {
      setActiveTab('creative')
    }
    defaultTabResolvedRef.current = true
  }, [isLoadingCampaign, tabVisibility.proposal, adUnitIdParam, activeTab])

  // Sync URL hash when active tab changes (ad units use URL path, not hash)
  useEffect(() => {
    if (activeTab === 'creativeStudio') return
    const hash = TAB_HASH_MAP[activeTab]
    if (hash) {
      const newHash = `#${hash}`
      if (location.hash !== newHash) {
        navigate(`${location.pathname}${location.search}${newHash}`, {
          replace: true,
          state: location.state ?? undefined,
        })
      }
    }
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync activeTab when navigating to/from ad unit route
  useEffect(() => {
    if (adUnitIdParam && activeTab !== 'creativeStudio') {
      setActiveTab('creativeStudio')
    } else if (!adUnitIdParam && activeTab === 'creativeStudio') {
      setActiveTab('creative')
    }
  }, [adUnitIdParam]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to parse budget
  const parseBudget = (budget: string): number => {
    if (!budget) return 100000
    const budgetStr = budget.replace(/[,$]/g, '').toLowerCase()
    if (budgetStr.includes('k')) {
      return parseInt(budgetStr.replace('k', '')) * 1000
    } else if (budgetStr.includes('m')) {
      return parseInt(budgetStr.replace('m', '')) * 1000000
    } else {
      return parseInt(budgetStr) || 100000
    }
  }

  // Helper function to parse KPI text and convert to structured KPIs
  const parseKPIFromText = (kpiText: string): any[] | null => {
    if (!kpiText) return null
    
    const kpis = []
    
    // Extract CTR information
    const ctrMatch = kpiText.match(/CTR.*?(\d+(?:\.\d+)?)/i)
    if (ctrMatch) {
      kpis.push({
        name: "Click-Through Rate",
        target: parseFloat(ctrMatch[1]) + 1,
        current: parseFloat(ctrMatch[1]),
        unit: "%",
        description: "Percentage of users who click on the ad",
        trend: "up"
      })
    }
    
    // Extract Conversion Rate information
    const conversionMatch = kpiText.match(/conversion.*?(\d+(?:\.\d+)?)/i)
    if (conversionMatch) {
      kpis.push({
        name: "Conversion Rate",
        target: parseFloat(conversionMatch[1]) + 1,
        current: parseFloat(conversionMatch[1]),
        unit: "%",
        description: "Percentage of users who complete the desired action",
        trend: "up"
      })
    }
    
    // Extract CPA information
    const cpaMatch = kpiText.match(/CPA.*?(\d+(?:\.\d+)?)/i)
    if (cpaMatch) {
      kpis.push({
        name: "Cost Per Acquisition",
        target: parseFloat(cpaMatch[1]) + 10,
        current: parseFloat(cpaMatch[1]),
        unit: "$",
        description: "Average cost to acquire a new customer",
        trend: "down"
      })
    }
    
    // Extract ROAS information
    const roasMatch = kpiText.match(/ROAS.*?(\d+(?:\.\d+)?)/i)
    if (roasMatch) {
      kpis.push({
        name: "Return on Ad Spend",
        target: parseFloat(roasMatch[1]) + 1,
        current: parseFloat(roasMatch[1]),
        unit: "x",
        description: "Return on advertising spend",
        trend: "up"
      })
    }
    
    // Return KPIs found in text, or empty array if none found (no hardcoded fallback)
    return kpis.length > 0 ? kpis : []
  }

  // @ts-expect-error kept for potential future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _transformApiResponseToCampaignData = (apiResponse: any, formData: any) => {
    const response = apiResponse.inputs || {}
    const mediaPlanData = response.media_plan || {}
    
    // Transform media_plan from API to our MediaPlan structure
    const transformedMediaPlan = {
      // Campaign Info
      advertiser: mediaPlanData.advertiser,
      agency: mediaPlanData.agency,
      client: mediaPlanData.client,
      campaign_name: mediaPlanData.campaign_name,
      campaign_period: mediaPlanData.campaign_period,
      duration: mediaPlanData.duration,
      market: mediaPlanData.market,
      
      // Channel & Creative Info
      channels: mediaPlanData.channels || [],
      creative_type: mediaPlanData.creative_type || [],
      creative_sizes: mediaPlanData.creative_sizes || [],
      targeting: mediaPlanData.targeting,
      
      // Placements
      placements: (mediaPlanData.placements || []).map((p: any) => ({
        channel: p.channel || '',
        format: p.format || '',
        duration: p.duration || '',
        cpm: p.cpm || 0,
        impressions: p.impressions || 0,
        cost: p.cost || 0,
        rationale: p.rationale || ''
      })),
      
      // Budget
      budget_allocation: mediaPlanData.budget_allocation ? {
        total_cost: mediaPlanData.budget_allocation.total_cost || 0,
        by_channel: mediaPlanData.budget_allocation.by_channel || []
      } : undefined,
      
      // Legacy budget format fallback
      budgetAllocation: response.budget?.map((budget: any) => ({
        channel: budget.channel_name || budget.channel || '',
        percentage: parseInt(budget.percentage_allocation?.replace('%', '') || budget.percentage?.toString() || '0'),
        amount: parseInt(budget.amount?.replace(/[$,]/g, '') || budget.amount?.toString() || '0'),
        rationale: budget.goal || budget.rationale || ''
      })) || [],
      
      // KPIs
      kpis: mediaPlanData.kpis ? {
        impressions: mediaPlanData.kpis.impressions || 0,
        clicks: mediaPlanData.kpis.clicks || 0,
        ctr: mediaPlanData.kpis.ctr || 0,
        reach_estimate: mediaPlanData.kpis.reach_estimate || '',
        frequency: mediaPlanData.kpis.frequency || ''
      } : undefined,
      
      // Site List (embedded in media_plan)
      sitelists: mediaPlanData.sitelists || [],
      
      // Summary & Recommendations
      summary: mediaPlanData.summary,
      category: mediaPlanData.category,
      strategic_recommendation: mediaPlanData.strategic_recommendation,
      creative_recommendation: mediaPlanData.creative_recommendation,
      
      // Legacy timeline
      timeline: response.timeline || []
    }
    
    return {
      id: `campaign_${Date.now()}`,
      objective: formData.objective || mediaPlanData.campaign_name || '',
      budget: parseBudget(formData.budget || '') || mediaPlanData.budget_allocation?.total_cost || 0,
      duration: formData.duration || mediaPlanData.campaign_period || '',
      targetAudience: formData.targetAudience || mediaPlanData.targeting || '',
      industry: formData.industry || '',
      channels: formData.channels || mediaPlanData.channels || [],
      overview: {
        summary: mediaPlanData.summary || response.overview || '',
        goals: response.objectives || [],
        keyMessages: response.creative_ideas ? [response.creative_ideas] : [],
        competitiveAdvantage: response.competitiveAdvantage || '',
        successMetrics: response.successMetrics || [],
        period: mediaPlanData.campaign_period || response.period || ''
      },
      mediaPlan: transformedMediaPlan,
      audiencePlan: {
        segments: response.audienceSegments || response.segments || [],
        personas: response.personas || [],
        targeting: response.targeting || {
          primary: [],
          secondary: [],
          lookalike: [],
          retargeting: []
        },
        // Map sitelists from media_plan for the SiteList planner
        siteList: mediaPlanData.sitelists && mediaPlanData.sitelists.length > 0 ? {
          description: `Site list for ${mediaPlanData.campaign_name || 'campaign'}`,
          totalSites: mediaPlanData.sitelists.length,
          sites: mediaPlanData.sitelists
        } : response.site_list ? {
          description: response.site_list.description || '',
          totalSites: response.site_list.totalSites || response.site_list.total_sites || 0,
          categories: response.site_list.categories || [],
          sites: response.site_list.sites || []
        } : undefined
      },
      adCopies: response.adCopies || response.ad_copies || [],
      creativeVariants: response.creativeVariants || response.creative_variants || [],
      templates: response.templates || [],
      highImpactTemplates: response.highImpactTemplates || response.high_impact_templates || [],
      kpis: parseKPIFromText(response.kpi) || [],
      dcoRetargeting: response.dcoRetargeting || response.dco_retargeting || {
        strategy: '',
        segments: [],
        creativeRules: [],
        optimization: []
      },
      programmaticTemplates: response.programmaticTemplates || response.programmatic_templates || [],
      doohTemplates: response.doohTemplates || response.dooh_templates || [],
      generatedAt: new Date(),
      // Store original API response for chat integration
      apiResponse: apiResponse
    }
  }

  /**
   * Extract the flat site list array from the nested sitelists response_state object.
   * Backend sends: { sitelists: { sitelists: { sitelists: [...] } } } or { sitelists: { sitelists: [...] } }
   */
  const extractSitelistsArray = (sitelistsObj: any): any[] => {
    if (!sitelistsObj) return []
    if (Array.isArray(sitelistsObj)) return sitelistsObj

    const inner = sitelistsObj.sitelists
    if (Array.isArray(inner)) return inner
    if (inner && typeof inner === 'object') {
      if (Array.isArray(inner.sitelists)) return inner.sitelists
    }
    return []
  }

  /**
   * Get the template recommendations array from either key variant.
   * Backend sends `template_recommendation` (singular) with `.recommendations` array.
   */
  const extractTemplateRecs = (rs: Record<string, any>): any[] => {
    const raw = rs.template_recommendation || rs.template_recommendations
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    if (raw.recommendations && Array.isArray(raw.recommendations)) return raw.recommendations
    return []
  }

  /**
   * Adapt the new `agentic_buy.get_product` payload into the existing
   * SalesRecommendations contract that the rest of the page already
   * understands (campaignData.salesRecommendations, hasSalesRecommendations
   * gate, MediaBuyTab products prop). Returns undefined when no products
   * have arrived yet so partial-data callers can short-circuit.
   */
  const extractSalesRecommendations = (rs: Record<string, any>): any | undefined => {
    const agenticBuy = rs.agentic_buy
    const getProduct = agenticBuy?.get_product
    const products = getProduct?.products
    if (!Array.isArray(products) || products.length === 0) return undefined

    return {
      success: true,
      data: {
        products,
        count: typeof getProduct.count === 'number' ? getProduct.count : products.length,
      },
      metadata: {
        taskId: agenticBuy?.task_id || '',
        status: agenticBuy?.status || 'completed',
      },
    }
  }

  /**
   * Adapt the new `agentic_buy.create_media_buy` payload into the existing
   * MediaBuyData contract that MediaBuyTab already understands. Only returns
   * a value once we have a media_buy_id + at least one package, otherwise
   * partial snapshots would prematurely flip the tab into Sync-creatives
   * mode.
   */
  const extractMediaBuyFromAgenticBuy = (
    rs: Record<string, any>,
  ): MediaBuyData | undefined => {
    const cmb = rs?.agentic_buy?.create_media_buy
    if (!cmb || typeof cmb !== 'object') return undefined
    const mediaBuyId = cmb.media_buy_id
    const packages = Array.isArray(cmb.packages) ? cmb.packages : []
    if (!mediaBuyId || packages.length === 0) return undefined

    return {
      media_buy_id: String(mediaBuyId),
      status: cmb.status ?? rs?.agentic_buy?.status ?? null,
      buyer_ref: cmb.buyer_ref ?? '',
      creative_deadline: cmb.creative_deadline ?? null,
      packages: packages.map((pkg: any) => ({
        package_id: pkg.package_id,
        product_id: pkg.product_id,
        pricing_option_id: pkg.pricing_option_id,
        budget: typeof pkg.budget === 'number' ? pkg.budget : Number(pkg.budget) || 0,
        buyer_ref: pkg.buyer_ref ?? '',
        paused: !!pkg.paused,
        canceled: !!pkg.canceled,
        impressions: typeof pkg.impressions === 'number' ? pkg.impressions : undefined,
        targeting_overlay: pkg.targeting_overlay ?? undefined,
        format_ids_to_provide: Array.isArray(pkg.format_ids_to_provide)
          ? pkg.format_ids_to_provide
              .filter((f: any) => f && typeof f.id === 'string')
              .map((f: any) => ({ id: f.id, agent_url: f.agent_url }))
          : undefined,
      })),
    }
  }

  /**
   * Build a partial CampaignData from streaming response_state snapshot.
   * Only populates fields for which data actually exists.
   */
  const buildPartialCampaignData = (rs: Record<string, any>): CampaignData | null => {
    const templateRecs = extractTemplateRecs(rs)
    const hasTemplates = templateRecs.length > 0
    const sitelistsArr = extractSitelistsArray(rs.sitelists)
    const hasSitelists = sitelistsArr.length > 0
    const salesRec = extractSalesRecommendations(rs)
    const hasSalesRec = !!salesRec
    const hasMP = rs.media_plan && typeof rs.media_plan === 'object' && Object.keys(rs.media_plan).length > 0

    if (!hasTemplates && !hasSitelists && !hasSalesRec && !hasMP) return null

    const templates = templateRecs.map((rec: any, i: number) => ({
      id: `template-${i}`,
      name: rec.ad_format?.format_id?.id || 'Unknown Template',
      type: rec.ad_format?.channel || 'display',
      format: rec.ad_format?.format_id?.id || '',
      resolution: '',
      description: rec.rationale || '',
      imageUrl: '',
      supportedTypes: [],
      usage: '',
      previewUrl: rec.ad_format?.preview_url || '',
      relevanceScore: undefined,
      complexityLevel: 'Medium' as const,
      benchmarkCtr: rec.expected_ctr || '',
      productionTime: '',
      availableSizes: rec.ad_format?.available_sizes || [],
      bestUseCases: rec.best_use_cases || [],
      cpm: rec.cpm || '',
      channel: rec.ad_format?.channel || ''
    }))

    const sitelists = sitelistsArr

    const mp = hasMP ? rs.media_plan : {}
    const mediaPlanBlock = hasMP ? {
      advertiser: mp.advertiser,
      agency: mp.agency,
      client: mp.client,
      campaign_name: mp.campaign_name,
      campaign_period: mp.campaign_period,
      duration: mp.duration,
      market: mp.market,
      channels: mp.channels || [],
      creative_type: mp.creative_type || [],
      creative_sizes: mp.creative_sizes || [],
      targeting: mp.targeting,
      placements: (mp.placements || []).map((p: any) => ({
        channel: p.channel || '',
        format: p.format || '',
        duration: p.duration || '',
        cpm: p.cpm || 0,
        impressions: p.impressions || 0,
        cost: p.cost || 0,
        rationale: p.rationale || ''
      })),
      budget_allocation: mp.budget_allocation ? {
        total_cost: mp.budget_allocation.total_cost || 0,
        by_channel: mp.budget_allocation.by_channel || []
      } : undefined,
      kpis: mp.kpis ? {
        impressions: mp.kpis.impressions || 0,
        clicks: mp.kpis.clicks || 0,
        ctr: mp.kpis.ctr || 0,
        reach_estimate: mp.kpis.reach_estimate || '',
        frequency: mp.kpis.frequency || ''
      } : undefined,
      sitelists: sitelists,
      summary: mp.summary,
      category: mp.category,
      strategic_recommendation: mp.strategic_recommendation,
      creative_recommendation: mp.creative_recommendation,
      timeline: []
    } : undefined

    return {
      id: campaignData?.id || `campaign_streaming_${Date.now()}`,
      objective: mp.campaign_name || campaignData?.objective || '',
      budget: mp.budget_allocation?.total_cost || campaignData?.budget || 0,
      duration: mp.duration || mp.campaign_period || campaignData?.duration || '',
      targetAudience: mp.targeting || campaignData?.targetAudience || '',
      industry: campaignData?.industry || '',
      channels: mp.channels || campaignData?.channels || [],
      overview: campaignData?.overview || {
        summary: mp.summary || '',
        goals: [],
        keyMessages: [],
        competitiveAdvantage: '',
        successMetrics: [],
        period: mp.campaign_period || ''
      },
      mediaPlan: mediaPlanBlock || (sitelists.length > 0
        ? { ...(campaignData?.mediaPlan || {} as any), sitelists }
        : campaignData?.mediaPlan || {} as any),
      audiencePlan: {
        segments: campaignData?.audiencePlan?.segments || [],
        personas: campaignData?.audiencePlan?.personas || [],
        targeting: campaignData?.audiencePlan?.targeting || {
          primary: [],
          secondary: [],
          lookalike: [],
          retargeting: []
        },
        siteList: sitelists.length > 0 ? {
          description: 'Site list',
          totalSites: sitelists.length,
          sites: sitelists
        } : campaignData?.audiencePlan?.siteList
      },
      adCopies: campaignData?.adCopies || [],
      creativeVariants: campaignData?.creativeVariants || [],
      templates: campaignData?.templates || [],
      highImpactTemplates: templates.length > 0 ? templates : (campaignData?.highImpactTemplates || []),
      kpis: campaignData?.kpis || [],
      dcoRetargeting: campaignData?.dcoRetargeting || {
        strategy: '',
        segments: [],
        creativeRules: [],
        optimization: []
      },
      programmaticTemplates: campaignData?.programmaticTemplates || [],
      doohTemplates: campaignData?.doohTemplates || [],
      generatedAt: campaignData?.generatedAt || new Date(),
      salesRecommendations: hasSalesRec ? salesRec : campaignData?.salesRecommendations,
      apiResponse: campaignData?.apiResponse
    }
  }

  /**
   * Deep-merge two CampaignData objects. Incoming non-null fields overwrite previous values.
   */
  const mergeCampaignData = (prev: CampaignData, incoming: CampaignData): CampaignData => {
    return {
      ...prev,
      ...incoming,
      id: prev.id || incoming.id,
      overview: incoming.overview?.summary ? incoming.overview : prev.overview,
      mediaPlan: incoming.mediaPlan || prev.mediaPlan,
      audiencePlan: {
        ...prev.audiencePlan,
        ...incoming.audiencePlan,
        siteList: incoming.audiencePlan?.siteList || prev.audiencePlan?.siteList
      },
      highImpactTemplates: incoming.highImpactTemplates && incoming.highImpactTemplates.length > 0
        ? incoming.highImpactTemplates
        : prev.highImpactTemplates,
      salesRecommendations: incoming.salesRecommendations || prev.salesRecommendations,
      apiResponse: prev.apiResponse || incoming.apiResponse
    }
  }

  /**
   * Transform conversation's campaign.media_plans data to CampaignData structure.
   * Also reads top-level response_state keys (template_recommendations, sitelists, agentic_buy).
   */
  const transformConversationToCampaignData = (conversation: any): CampaignData | null => {
    const campaign = conversation?.campaign
    const responseState = conversation?.response_state || {}

    // If no campaign or no media_plans yet, try building from response_state alone
    const mediaPlan = campaign?.media_plans?.[0]
    if (!mediaPlan) {
      return buildPartialCampaignData(responseState)
    }
    
    const mediaPlanData = mediaPlan.media_plan_data || mediaPlan

    // Templates: prefer top-level response_state key (both singular/plural), then media_plan_data
    const topLevelTemplateRecs = extractTemplateRecs(responseState)
    const mediaPlanTemplateRecs = mediaPlanData.template_recommendation?.recommendations || []
    const templateRecs = topLevelTemplateRecs.length > 0
      ? topLevelTemplateRecs
      : mediaPlanTemplateRecs

    // Sitelists: prefer top-level response_state key (nested object), then media_plan_data
    const topLevelSitelists = extractSitelistsArray(responseState.sitelists)
    const mediaPlanSitelists = extractSitelistsArray(mediaPlanData.sitelists)
    const sitelists = topLevelSitelists.length > 0 ? topLevelSitelists : mediaPlanSitelists

    // Agentic buy: read from top-level response_state.agentic_buy via the
    // adapter; legacy media_plan-nested sales_recommendations fallback is no
    // longer emitted by the backend.
    const salesRecommendations = extractSalesRecommendations(responseState)

    const transformedMediaPlan = {
      advertiser: mediaPlanData.advertiser || conversation.advertiser?.name,
      agency: mediaPlanData.agency || conversation.agency?.name,
      client: mediaPlanData.client,
      campaign_name: mediaPlanData.campaign_name || mediaPlan.campaign_name,
      campaign_period: mediaPlanData.campaign_period || mediaPlan.campaign_period,
      duration: mediaPlanData.duration,
      market: mediaPlanData.market || mediaPlan.market,
      channels: mediaPlanData.channels || campaign.channels || [],
      creative_type: mediaPlanData.creative_type || [],
      creative_sizes: mediaPlanData.creative_sizes || [],
      targeting: mediaPlanData.targeting,
      placements: (mediaPlanData.placements || mediaPlan.placements || []).map((p: any) => ({
        channel: p.channel || '',
        format: p.format || '',
        duration: p.duration || '',
        cpm: p.cpm || 0,
        impressions: p.impressions || 0,
        cost: p.cost || 0,
        rationale: p.rationale || ''
      })),
      budget_allocation: mediaPlanData.budget_allocation || mediaPlan.budget_allocation ? {
        total_cost: (mediaPlanData.budget_allocation || mediaPlan.budget_allocation)?.total_cost || mediaPlan.total_cost || 0,
        by_channel: (mediaPlanData.budget_allocation || mediaPlan.budget_allocation)?.by_channel || []
      } : undefined,
      kpis: mediaPlanData.kpis || mediaPlan.kpis ? {
        impressions: (mediaPlanData.kpis || mediaPlan.kpis)?.impressions || 0,
        clicks: (mediaPlanData.kpis || mediaPlan.kpis)?.clicks || 0,
        ctr: (mediaPlanData.kpis || mediaPlan.kpis)?.ctr || 0,
        reach_estimate: (mediaPlanData.kpis || mediaPlan.kpis)?.reach_estimate || '',
        frequency: (mediaPlanData.kpis || mediaPlan.kpis)?.frequency || ''
      } : undefined,
      sitelists: sitelists,
      summary: mediaPlanData.summary || mediaPlan.summary,
      category: mediaPlanData.category,
      strategic_recommendation: mediaPlanData.strategic_recommendation || mediaPlan.strategic_recommendation,
      creative_recommendation: mediaPlanData.creative_recommendation || mediaPlan.creative_recommendation,
      timeline: []
    }
    
    const highImpactTemplates = (Array.isArray(templateRecs) ? templateRecs : []).map((rec: any, index: number) => ({
      id: `template-${index}`,
      name: rec.ad_format?.format_id?.id || 'Unknown Template',
      type: rec.ad_format?.channel || 'display',
      format: rec.ad_format?.format_id?.id || '',
      resolution: '',
      description: rec.rationale || '',
      imageUrl: '',
      supportedTypes: [],
      usage: '',
      previewUrl: rec.ad_format?.preview_url || '',
      relevanceScore: undefined,
      complexityLevel: 'Medium' as const,
      benchmarkCtr: rec.expected_ctr || '',
      productionTime: '',
      availableSizes: rec.ad_format?.available_sizes || [],
      bestUseCases: rec.best_use_cases || [],
      cpm: rec.cpm || '',
      channel: rec.ad_format?.channel || ''
    }))
    
    return {
      id: `campaign_${campaign.id || Date.now()}`,
      objective: mediaPlanData.campaign_name || campaign.campaign_name || '',
      budget: mediaPlanData.budget_allocation?.total_cost || mediaPlan.total_cost || 0,
      duration: mediaPlanData.duration || mediaPlanData.campaign_period || '',
      targetAudience: mediaPlanData.targeting || '',
      industry: '',
      channels: mediaPlanData.channels || campaign.channels || [],
      overview: {
        summary: mediaPlanData.summary || mediaPlan.summary || '',
        goals: campaign.objectives || [],
        keyMessages: campaign.creative_ideas ? [campaign.creative_ideas] : [],
        competitiveAdvantage: '',
        successMetrics: [],
        period: mediaPlanData.campaign_period || mediaPlan.campaign_period || ''
      },
      mediaPlan: transformedMediaPlan,
      audiencePlan: {
        segments: [],
        personas: [],
        targeting: {
          primary: [],
          secondary: [],
          lookalike: [],
          retargeting: []
        },
        siteList: sitelists.length > 0 ? {
          description: `Site list for ${mediaPlanData.campaign_name || 'campaign'}`,
          totalSites: sitelists.length,
          sites: sitelists
        } : undefined
      },
      adCopies: [],
      creativeVariants: [],
      templates: [],
      highImpactTemplates: highImpactTemplates,
      kpis: [],
      dcoRetargeting: {
        strategy: '',
        segments: [],
        creativeRules: [],
        optimization: []
      },
      programmaticTemplates: [],
      doohTemplates: [],
      generatedAt: new Date(campaign.created_at || Date.now()),
      salesRecommendations: salesRecommendations,
      apiResponse: { conversation }
    }
  }

  // Helper function to convert conversation chat_history to ChatMessage format
  const convertChatHistoryToMessages = (chatHistory: Array<{ role: string; message?: string; content?: string }>): ChatMessage[] => {
    return chatHistory
      .filter(item => isDisplayableMessage(item as any))
      .map((item, index) => {
        const messageContent = item.message || item.content || ''
        const isUser = isUserMessage(item as any)
        const isCampaign = isCampaignAgentMessage(item as any)
        
        // Format campaign agent messages with structured content
        const formattedContent = isCampaign 
          ? formatCampaignAgentMessage(messageContent)
          : messageContent
        
        return {
          id: `history-${index}`,
          type: isUser ? 'user' as const : 'ai' as const,
          content: formattedContent,
          timestamp: new Date()
        }
      })
  }

  // Load accumulated inputs from location state (chat history is loaded from conversation API)
  useEffect(() => {
    const stateData = location.state

    // Check if chat messages were passed via location state (from Home.tsx navigation)
    if (stateData?.chatMessages && Array.isArray(stateData.chatMessages)) {
      setChatMessages(stateData.chatMessages)
    }

    // Initialize orchestrator running state from navigation. Home sets
    // `isStreaming: !isWorkflowComplete(responseState)` when the user clicks
    // "Go to Campaign" mid-run, so we light up both the proposal-tab
    // skeleton and the chat thinking bubble immediately on mount instead
    // of waiting for the first post-mount WS frame (which can take a few
    // seconds, leaving the chat looking idle even though work is in flight).
    if (stateData?.isStreaming) {
      setIsOrchestratorRunning(true)
      setIsChatLoading(true)
    }

    // Seed accumulated inputs from navigation state when provided.
    //
    // We intentionally do NOT fall back to `loadInputsFromStorage()` here
    // like older code did. `STORAGE_KEY = 'accumulatedInputs'` is a single
    // global bucket shared across every campaign/conversation the user
    // has ever opened, so falling back to it made proposal-shaped
    // leftovers from a previous campaign light up the "Campaign
    // Proposal" tab on a different campaign that has no proposal data
    // of its own. The correct scoped source is `conversation.response_state`,
    // which `fetchCampaignData` below wires straight into
    // `accumulatedInputs` when navigation state is absent.
    if (stateData?.apiResponse?.inputs) {
      const inputs = stateData.apiResponse.inputs as AccumulatedInputs
      setAccumulatedInputs(inputs)
      saveInputsToStorage(inputs)
    }
  }, [location.state])

  // Get campaign data from API using campaign ID → conversation ID → rich media plan data
  useEffect(() => {
    const stateData = location.state
    
    /**
     * Main data fetching flow:
     * 1. Fetch /campaigns/:id to get conversation_id
     * 2. Fetch /ai/conversations/:conversation_id to get rich media_plan data
     * 3. Transform and display
     */
    const fetchCampaignData = async () => {
      setIsLoadingCampaign(true)

      // Whether the caller pre-seeded `accumulatedInputs` via navigation
      // state (the "handleViewResults" path from Home). When they did, we
      // must not overwrite it with the REST response_state on this pass —
      // the caller's snapshot is the fresher, mid-run truth. Every other
      // entry (View Campaign, campaign card, direct URL) has no
      // navigation state and needs the API to seed inputs so tab
      // visibility reflects THIS campaign, not whatever the previous one
      // left in state.
      const hasNavStateInputs = Boolean(stateData?.apiResponse?.inputs)

      // Reset per-campaign flags before fetching. Without this, opening
      // /campaigns/A (proposal-running) then navigating to /campaigns/B
      // would keep the proposal-tab skeleton lit on B because
      // `isOrchestratorRunning` from A was never cleared. We skip the
      // reset when the incoming navigation explicitly says a stream is
      // in flight (`stateData.isStreaming`) — the sibling location.state
      // effect just set it to `true` on purpose and we mustn't clobber
      // that.
      if (!stateData?.isStreaming) {
        setIsOrchestratorRunning(false)
      }

      // If we have campaignId from URL, fetch fresh data from API
      if (campaignId) {
        try {
          console.log('Fetching campaign:', campaignId)
          
          // Step 1: Get campaign to find conversation_id
          const campaign = await campaignApi.getCampaign(parseInt(campaignId, 10))
          if (campaign.advertiser_id) setAdvertiserId(campaign.advertiser_id)
          
          // Check if campaign has a linked conversation
          const fetchedConversationId = (campaign as any).conversation_id
          
          if (fetchedConversationId) {
            console.log('Found conversation_id:', fetchedConversationId, 'for campaign:', campaignId)
            
            // Store conversation ID for WebSocket subscription
            setConversationId(fetchedConversationId)
            
            // Step 2: Fetch conversation with rich media_plan data
            const conversation = await conversationApi.getConversation(fetchedConversationId)
            
            // Step 3: Load chat history from conversation (only if not already loaded from location.state)
            if (conversation.chat_history && conversation.chat_history.length > 0) {
              const messages = convertChatHistoryToMessages(conversation.chat_history)
              if (messages.length > 0) {
                setChatMessages(messages)
                console.log('Chat history loaded from conversation:', messages.length, 'messages')
              }
            }
            
            // Detect if orchestrator is still running from REST response.
            // We deliberately do NOT treat "conversation has no response_state"
            // or "response_state is just a scaffold without next_node" as
            // running — manually-created campaigns land here with an empty
            // conversation and would otherwise show the "Generating Media
            // Planner…" skeleton even though nothing was ever triggered.
            const convStatus = (conversation as any).status
            const convResponseState = (conversation as any).response_state
            // `convStatus` alone is unreliable here — a freshly-created
            // conversation with no workflow ever invoked can also be in
            // `pending`. The presence of a mid-flight `next_node` is the
            // only signal we trust.
            void convStatus
            if (hasActiveOrchestratorSignal(convResponseState)) {
              setIsOrchestratorRunning(true)
            }

            // Seed the chat thinking bubble from the REST snapshot so a
            // direct deep-link to /campaigns/:id (no navigation state) still
            // shows "AI is working" when the orchestrator is mid-flight.
            // After this, the live useChat WS subscription keeps the flag
            // in sync via its own shouldShowThinking call.
            setIsChatLoading(
              shouldShowThinking(
                convResponseState as any,
                conversation.chat_history as any,
                typeof convStatus === 'string' ? convStatus : '',
              ),
            )

            // Seed accumulatedInputs from this conversation's own
            // response_state so tab visibility (Campaign Proposal in
            // particular) reflects THIS campaign's data. Without this,
            // the value carried over from whatever the last campaign
            // set (or from stale localStorage) — that's why the
            // "Campaign Proposal" menu leaked across conversations.
            //
            // The nav-state guard skips this branch when the caller
            // (Home's handleViewResults) already passed the freshest
            // inputs to us — replacing them with the REST snapshot
            // would lose in-flight fields the WS hasn't fetched yet.
            if (!hasNavStateInputs) {
              const seedInputs = (convResponseState ?? {}) as AccumulatedInputs
              setAccumulatedInputs(seedInputs)
              saveInputsToStorage(seedInputs)
            }

            // Step 4: Transform to CampaignData
            const transformedData = transformConversationToCampaignData(conversation)

            if (transformedData) {
              setCampaignData(transformedData)
              console.log('Campaign data loaded from API:', { campaignId, conversationId: fetchedConversationId })
              setIsLoadingCampaign(false)
              return
            }
          } else {
            console.log('No conversation_id found for campaign:', campaignId)
          }
        } catch (error) {
          console.error('Failed to fetch campaign data from API:', error)
        }
      }
      
      // Fallback: If conversationId passed in navigation state (from Home.tsx)
      if (stateData?.conversationId) {
        try {
          // Store conversation ID for WebSocket subscription
          setConversationId(stateData.conversationId)
          
          const conversation = await conversationApi.getConversation(stateData.conversationId)
          
          // Load chat history from conversation (only if not already loaded from location.state)
          if (conversation.chat_history && conversation.chat_history.length > 0 && !stateData?.chatMessages) {
            const messages = convertChatHistoryToMessages(conversation.chat_history)
            if (messages.length > 0) {
              setChatMessages(messages)
              console.log('Chat history loaded from conversation (fallback):', messages.length, 'messages')
            }
          }
          
          // Detect orchestrator state from fallback conversation fetch.
          // Same guard as above: avoid flagging a fresh / empty conversation
          // as "running" just because it lacks a terminal next_node.
          const fallbackStatus = (conversation as any).status
          const fallbackRS = (conversation as any).response_state
          // Same reasoning as the primary branch above: trust `next_node`
          // signal, not the coarse-grained conversation `status`, otherwise
          // a manually-created campaign with a `pending` conversation
          // would show the proposal loading skeleton forever.
          void fallbackStatus
          if (hasActiveOrchestratorSignal(fallbackRS)) {
            setIsOrchestratorRunning(true)
          }

          // Same chat-thinking seed as the primary branch — when the user
          // reaches this page via the fallback conversationId path the
          // first WS frame may still be seconds away, so we render the
          // thinking bubble eagerly based on the REST snapshot.
          setIsChatLoading(
            shouldShowThinking(
              fallbackRS as any,
              conversation.chat_history as any,
              typeof fallbackStatus === 'string' ? fallbackStatus : '',
            ),
          )

          // Same reasoning as the primary branch: seed inputs from
          // THIS conversation's response_state when the caller didn't
          // pre-seed via navigation state. Otherwise leftover proposal
          // flags from a previous campaign light up the wrong tab.
          if (!hasNavStateInputs) {
            const seedFallbackInputs = (fallbackRS ?? {}) as AccumulatedInputs
            setAccumulatedInputs(seedFallbackInputs)
            saveInputsToStorage(seedFallbackInputs)
          }

          const transformedData = transformConversationToCampaignData(conversation)

          if (transformedData) {
            setCampaignData(transformedData)
            console.log('Campaign data loaded from conversation state:', { conversationId: stateData.conversationId })
            setIsLoadingCampaign(false)
            return
          }
        } catch (error) {
          console.error('Failed to fetch conversation from state:', error)
        }
      }
      
      // REST transforms returned null — seed campaignData from navigation state if available
      if (stateData?.apiResponse?.inputs) {
        const partial = buildPartialCampaignData(stateData.apiResponse.inputs)
        if (partial) {
          setCampaignData(partial)
          console.log('Campaign data seeded from navigation state (response_state)')
          setIsLoadingCampaign(false)
          return
        }
      }
      
      console.log('No campaign data available for:', { campaignId })
      setIsLoadingCampaign(false)
    }
    
    fetchCampaignData()
  }, [campaignId, location.state])

  // Unified chat hook — manages WS subscription, messages, send, FormData
  const {
    chatMessages,
    setChatMessages,
    isChatLoading,
    setIsChatLoading,
    sendMessage: chatSendMessage,
  } = useChat({
    advertiserId: advertiserId,
    conversationId: conversationId,
    onConversationCreated: (id, _conv) => setConversationId(id),
    onResponseStateUpdate: (rs: Record<string, unknown>) => {
      if (isWorkflowComplete(rs as any)) {
        setIsOrchestratorRunning(false)
        // Some workflows (e.g. dynamic_template_creator) finish via
        // conversation_update with next_node === 'end' WITHOUT emitting an
        // ai_creative_creation_completed event, so we'd otherwise leave the
        // AI Creative modal stuck on its loading screen forever. Tear it down
        // here as a workflow-agnostic fallback.
        if (isCreatingAdUnitsRef.current) {
          setIsCreatingAdUnits(false)
          setShowAICreativeModal(false)
        }
      }

      // ai_creative_generation container is the new completion signal for
      // the Start-with-AI flow. The BE no longer emits a dedicated cable
      // event; it persists the ad unit and surfaces its id here. We fetch
      // the unit, mirror it into completedAdUnits, then deep-link the user
      // into the Creative Studio for that ad unit.
      const aiGen = (rs as any).ai_creative_generation
      const generatedAdUnitId =
        typeof aiGen?.ad_unit_id === 'number' ? aiGen.ad_unit_id : undefined
      if (
        generatedAdUnitId &&
        isWorkflowComplete(rs as any) &&
        aiCreativeNavigatedAdUnitIdRef.current !== generatedAdUnitId
      ) {
        aiCreativeNavigatedAdUnitIdRef.current = generatedAdUnitId
        setIsCreatingAdUnits(false)
        setShowAICreativeModal(false)
        setHasApiAdUnits(true)

        const numericCampaignId = campaignId ? parseInt(campaignId, 10) : undefined
        if (!numericCampaignId || Number.isNaN(numericCampaignId)) {
          console.warn(
            'ai_creative_generation completed but campaignId is missing from URL; cannot redirect',
            { generatedAdUnitId, campaignId },
          )
          toast.error('Creative generated but campaign context is missing.')
        } else {
          // Fire-and-forget detail fetch so the Creative Studio has fresh
          // data. Navigation happens regardless — Studio will refetch on
          // mount if this fails.
          adUnitsApi
            .getAdUnit(numericCampaignId, generatedAdUnitId)
            .then(adUnit => {
              setCompletedAdUnits(prev => {
                if (prev.some(u => u.ad_unit_id === generatedAdUnitId)) return prev
                return [
                  ...prev,
                  {
                    ad_unit_id: generatedAdUnitId,
                    campaign_id: numericCampaignId,
                    content_type: adUnit?.content_type ?? '',
                    sizes: Array.isArray(adUnit?.sizes) ? adUnit.sizes : [],
                  },
                ]
              })
            })
            .catch(err => {
              console.warn('Failed to prefetch generated ad unit details', err)
            })

          toast.success('Creative generated successfully!')
          navigate(`/campaigns/${numericCampaignId}/ad_units/${generatedAdUnitId}`, {
            state: location.state ?? undefined,
          })
        }
      }

      setAccumulatedInputs(prev => {
        const next = { ...prev }
        const templateData = (rs as any).template_recommendation || (rs as any).template_recommendations
        if (templateData) {
          next.template_recommendation = templateData
          next.template_recommendations = templateData
          next.template_recommender = templateData
        }
        const sitelistsObj = (rs as any).sitelists
        if (sitelistsObj && typeof sitelistsObj === 'object') {
          next.sitelists = sitelistsObj
          next.site_list = sitelistsObj
        }
        // Mirror the agentic_buy container so detectAvailableAgents can read
        // it and light up the Media Buy tab as soon as get_product.products
        // arrives. Replaces the legacy sales_recommendation(s) plumbing.
        const agenticBuy = (rs as any).agentic_buy
        if (agenticBuy && typeof agenticBuy === 'object') {
          next.agentic_buy = agenticBuy
        }
        const aiGenForInputs = (rs as any).ai_creative_generation
        if (aiGenForInputs && typeof aiGenForInputs === 'object') {
          next.ai_creative_generation = aiGenForInputs
        }

        // When agentic_buy.create_media_buy arrives, mirror it into the
        // MediaBuyData state shape so MediaBuyTab flips into "Sync creatives"
        // mode (same UX the legacy media_buy_complete cable event drove).
        const mb = extractMediaBuyFromAgenticBuy(rs as any)
        if (mb) {
          setMediaBuyData(prev => {
            // Only replace when something actually changed; React will skip
            // re-renders, but this also prevents repeated toasts.
            if (prev && prev.media_buy_id === mb.media_buy_id && prev.packages.length === mb.packages.length) {
              return mb
            }
            return mb
          })
          const productIds = Array.from(new Set(mb.packages.map(p => p.product_id))).filter(Boolean)
          setSelectedProductIds(productIds)
          setIsCreatingMediaBuy(false)
        }
        if ((rs as any).media_plan && typeof (rs as any).media_plan === 'object' && Object.keys((rs as any).media_plan).length > 0) {
          next.media_plan = (rs as any).media_plan
        }
        return next
      })

      setCampaignData(prev => {
        const partial = buildPartialCampaignData(rs as any)
        if (!partial) return prev
        if (!prev) return partial
        return mergeCampaignData(prev, partial)
      })
    },
    extraSubscriptionCallbacks: {
      onAdUnitProcessing: (data: PreviewCreativeStatusData) => {
        if (data.data.status === 'processing' && data.data.ad_unit) {
          setProcessingAdUnits(prev => {
            const exists = prev.some(u => u.id === data.data.ad_unit.id)
            if (exists) return prev
            return [...prev, data.data.ad_unit]
          })
          setActiveTab('creative')
          toast.success(`Creating ad unit: ${data.data.ad_unit.content_type}`)
        } else if (data.data.status === 'failed') {
          setIsCreatingAdUnits(false)
          toast.error('Failed to create ad unit. Please try again.')
        }
      },
      onAdUnitCreated: (data: PreviewCreativeCreatedData) => {
        if (data.data.ad_unit) {
          const createdUnit = data.data.ad_unit
          setProcessingAdUnits(prev => prev.filter(u => u.id !== createdUnit.ad_unit_id))
          setCompletedAdUnits(prev => {
            const exists = prev.some(u => u.ad_unit_id === createdUnit.ad_unit_id)
            if (exists) return prev.map(u => u.ad_unit_id === createdUnit.ad_unit_id ? createdUnit : u)
            return [...prev, createdUnit]
          })
          setProcessingAdUnits(current => {
            if (current.length === 0) setIsCreatingAdUnits(false)
            return current
          })
          toast.success(`Ad unit ready: ${createdUnit.content_type}`)
        }
      },
      onMediaBuyComplete: (data: MediaBuyCompleteData) => {
        setIsCreatingMediaBuy(false)
        if (data.data.success && data.data.data) {
          setMediaBuyData(data.data.data)
          const productIds = data.data.data.packages.map(pkg => pkg.product_id)
          setSelectedProductIds(productIds)
          toast.success(`Media buy created: ${data.data.data.media_buy_id}`)
        } else {
          toast.error('Failed to create media buy. Please try again.')
        }
      },
      onCreativeSyncCompleted: (data: CreativeSyncCompletedData) => {
        if (data.payload.success && data.payload.data.creatives) {
          setSyncedCreatives(prev => [...prev, ...data.payload.data.creatives])
          const count = data.payload.data.creatives.length
          toast.success(`${count} creative${count !== 1 ? 's' : ''} synced successfully!`)
        } else {
          toast.error('Failed to sync creative. Please try again.')
        }
      },
      onAiCreativeCreationCompleted: (data: AiCreativeCreationCompletedData) => {
        setIsCreatingAdUnits(false)
        setHasApiAdUnits(true)
        setShowAICreativeModal(false)

        const adUnit = data?.data?.ad_unit
        if (!adUnit || !adUnit.id || !adUnit.campaign_id) {
          console.warn('ai_creative_creation_completed: missing ad_unit in payload', data)
          toast.error('Creative generated but no ad unit info returned.')
          return
        }

        setCompletedAdUnits(prev => {
          const exists = prev.some(u => u.ad_unit_id === adUnit.id)
          if (exists) return prev
          return [
            ...prev,
            {
              ad_unit_id: adUnit.id,
              campaign_id: adUnit.campaign_id,
              content_type: adUnit.content_type,
              sizes: adUnit.sizes ?? [],
            },
          ]
        })

        toast.success('Creative generated successfully!')
        navigate(`/campaigns/${adUnit.campaign_id}/ad_units/${adUnit.id}`, {
          state: location.state ?? undefined,
        })
      },
    },
  })

  // Check if campaign has ad units from API (for tab visibility on page refresh)
  useEffect(() => {
    if (!campaignId) return
    
    const checkAdUnits = async () => {
      try {
        const { adUnits } = await adUnitsApi.getAdUnits(parseInt(campaignId, 10))
        setHasApiAdUnits(adUnits.length > 0)
        console.log('Campaigns: Ad units check:', adUnits.length, 'units found')
      } catch (error) {
        console.log('Campaigns: No ad units found or error fetching:', error)
        setHasApiAdUnits(false)
      }
    }
    
    checkAdUnits()
  }, [campaignId])

  // No redirect - stay on /campaigns/:id and show page structure

  const renderTabContent = () => {
    // Creative tab always renders when we have a campaignId (for existing campaigns with ad units)
    if (activeTab === 'creative') {
      return (
        <CreativeGenerationTab 
          campaignId={campaignId ? parseInt(campaignId, 10) : undefined}
          advertiserId={advertiserId}
          processingAdUnits={processingAdUnits}
          completedAdUnits={completedAdUnits}
          isLoading={isCreatingAdUnits}
          onOpenStudio={() => setShowAICreativeModal(true)}
          onOpenAdUnit={(id: number) => {
            navigate(`/campaigns/${campaignId}/ad_units/${id}`, {
              state: location.state ?? undefined,
            })
          }}
          onViewDashboard={(id: number) => {
            navigate(`/dashboard/campaign/${campaignId}/ad_unit/${id}`)
          }}
        />
      )
    }

    // Creative Studio: editor for a single ad unit
    if (activeTab === 'creativeStudio') {
      return (
        <CreativeStudioTab
          campaignId={campaignId ? parseInt(campaignId, 10) : undefined}
          adUnitId={activeAdUnitId ?? undefined}
          /* Always return to this campaign's Creative Generation list,
             regardless of how the user arrived in the studio (deep-link
             from chat, direct URL, etc.). Using navigate(-1) was unreliable
             because it could pop the user back to /home or an unrelated
             page when there was no in-app history yet. */
          onBack={() =>
            navigate(`/campaigns/${campaignId}#creativegeneration`, {
              state: location.state ?? undefined,
            })
          }
        />
      )
    }

    // Note: do NOT early-return null when there is no campaign data — each tab
    // is responsible for rendering its own empty state (e.g. MediaBuy shows a
    // "Browse Products" CTA, Proposal shows starter guidance). Returning null
    // here used to leave the entire right pane blank for old/empty campaigns.

    switch (activeTab) {
      case 'proposal':
        return (
          <CampaignProposalTab
            data={campaignData}
            activeSubTab={activeSubTab}
            onSubTabChange={setActiveSubTab}
            onCreateAdUnits={handleCreateAdUnits}
            isOrchestratorRunning={isOrchestratorRunning}
          />
        )
      case 'mediaBuy':
        return (
          <MediaBuyTab
            products={campaignData?.salesRecommendations?.data?.products || []}
            conversationId={conversationId}
            campaignId={campaignId ? parseInt(campaignId, 10) : null}
            onCreateMediaBuy={handleCreateMediaBuy}
            isCreating={isCreatingMediaBuy}
            mediaBuyData={mediaBuyData}
            selectedProductIds={selectedProductIds}
            syncedCreatives={syncedCreatives}
            onSendMessage={(msg: string) => chatSendMessage(msg, { workflow: 'agentic_buy' })}
          />
        )
      case 'insights':
        if (!campaignData?.reportInsights) {
          return (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center">
                <p className="text-gray-500 dark:text-gray-400">No insights data available yet.</p>
              </div>
            </div>
          )
        }
        return <ReportInsightsTab data={campaignData.reportInsights} />
      default:
        return (
          <CampaignProposalTab
            data={campaignData}
            activeSubTab={activeSubTab}
            onSubTabChange={setActiveSubTab}
            isOrchestratorRunning={isOrchestratorRunning}
          />
        )
    }
  }

  // Handle create ad units click - gates the call behind the optional assets modal
  const handleCreateAdUnits = (templateIds: string[], sizes: Map<string, Set<string>>) => {
    if (!conversationId) {
      toast.error('No conversation linked to this campaign')
      return
    }

    if (templateIds.length === 0) {
      toast.error('Please select at least one template')
      return
    }

    // Transform template selections to API format
    // Template name (e.g., "3d_cube", "countdown") becomes content_type
    const selectedTemplates: SelectedTemplate[] = templateIds.map(templateId => {
      const allTemplates = [
        ...(campaignData?.highImpactTemplates || []),
        ...(campaignData?.programmaticTemplates || []),
        ...(campaignData?.doohTemplates || [])
      ]
      const template = allTemplates.find(t => t.id === templateId)
      const contentType = template?.name || template?.format || templateId
      const templateSizes = sizes.get(templateId)
      const sizesArray = templateSizes ? Array.from(templateSizes) : []
      return { content_type: contentType, sizes: sizesArray }
    })

    setSelectedTemplateIds(templateIds)
    setSelectedSizes(sizes)
    setPendingCreateAdUnits(selectedTemplates)
    setIsAssetsModalOpen(true)
  }

  // Actually fire the create-ad-units request (optionally with uploaded assets)
  const performCreateAdUnits = async (
    selectedTemplates: SelectedTemplate[],
    files?: File[]
  ) => {
    if (!conversationId) {
      toast.error('No conversation linked to this campaign')
      return
    }

    setIsCreatingAdUnits(true)

    try {
      console.log('Creating ad units with payload:', {
        conversationId,
        selectedTemplates,
        assetCount: files?.length ?? 0,
      })

      await conversationApi.createAdUnits(conversationId, selectedTemplates, files)

      // Response will come via WebSocket (preview_creative_update)
      toast.success('Creating ad units... Please wait.')
    } catch (error) {
      console.error('Failed to create ad units:', error)
      setIsCreatingAdUnits(false)
      toast.error('Failed to create ad units. Please try again.')
    }
  }

  const handleAssetsModalSkip = () => {
    const pending = pendingCreateAdUnits
    setIsAssetsModalOpen(false)
    setPendingCreateAdUnits(null)
    if (pending) {
      performCreateAdUnits(pending, undefined)
    }
  }

  const handleAssetsModalSubmit = (files: File[]) => {
    const pending = pendingCreateAdUnits
    setIsAssetsModalOpen(false)
    setPendingCreateAdUnits(null)
    if (pending) {
      performCreateAdUnits(pending, files.length > 0 ? files : undefined)
    }
  }

  // Handle create media buy. The MediaBuyTab now collects budget/brand/
  // domain/pricing via a small modal and forwards them as `extras` so we can
  // build the full agentic_buy payload here.
  const handleCreateMediaBuy = async (
    selectedProducts: import('../types').SalesProduct[],
    extras: {
      budget: string
      brand_id: string
      domain: string
      pricing_option_id: string
    },
  ) => {
    if (!conversationId) {
      toast.error('No conversation linked to this campaign')
      return
    }

    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product')
      return
    }

    setIsCreatingMediaBuy(true)

    try {
      const numericCampaignId = campaignId ? parseInt(campaignId, 10) : undefined
      const apiExtras = {
        ...extras,
        workflow: 'agentic_buy',
        ...(numericCampaignId && !Number.isNaN(numericCampaignId)
          ? { campaign_id: numericCampaignId }
          : {}),
      }

      console.log('Creating media buy with payload:', {
        conversationId,
        products: selectedProducts,
        ...apiExtras,
      })

      await conversationApi.createMediaBuy(conversationId, selectedProducts, apiExtras)

      // Response will come via WebSocket
      toast.success('Creating media buy... Please wait.')

    } catch (error) {
      console.error('Failed to create media buy:', error)
      setIsCreatingMediaBuy(false)
      toast.error('Failed to create media buy. Please try again.')
    }
  }

  // Handle PDF download
  // Handle form submission (regenerate campaign)
  const handleGenerateStrategy = async (formData: any) => {
    setCurrentFormData(formData)
    // Navigate back to home to generate new campaign
    navigate('/', { state: { formData } })
  }

  // Handle chat message with streaming
  const handleChatMessage = async (message: string, _files?: File[]) => {
    setCurrentChatInput(message)
    try {
      const extra = activeTab === 'creativeStudio'
        ? { workflow: 'ai_creative_generation', content_size: studioSelectedSizes }
        : undefined
      await chatSendMessage(message || 'Continue', extra)
    } catch {
      toast.error('Failed to send message. Please try again.')
    } finally {
      setCurrentChatInput('')
    }
  }

  // Handle size picker confirmation: switch to studio + auto-send first creative request
  // Handle AI Creative modal generate
  const handleAICreativeGenerate = async (payload: AICreativePayload) => {
    setStudioSelectedSizes(payload.content_size)
    setIsCreatingAdUnits(true)

    const hasFiles = payload.reference_images && payload.reference_images.length > 0

    const displaySizes = payload.content_size.join(', ')
    const queryMsg = payload.query !== 'Start creating/generating creative or ad HTML5 template'
      ? `${payload.query} (${displaySizes})`
      : `Start creating template/creative (${displaySizes})`

    try {
      if (hasFiles) {
        // Multipart shape mirrors the working `createAdUnits` flow:
        // every scalar lives under `conversation[...]`, files go in a top-level
        // `reference_images[]` array. This matches the Rails strong-params
        // contract that the JSON path shimming through nested keys also hits.
        const fd = new FormData()
        fd.append('conversation[workflow]', 'ai_creative_generation')
        fd.append('conversation[status]', 'input_received')
        fd.append('conversation[query]', queryMsg)

        payload.content_size.forEach((s) =>
          fd.append('conversation[content_size][]', s)
        )

        if (!conversationId && campaignId) {
          fd.append('conversation[campaign_id]', String(parseInt(campaignId, 10)))
        }
        if (payload.brand_kit_id) {
          fd.append('conversation[brand_kit_id]', String(payload.brand_kit_id))
        }
        if (payload.destination_url) {
          fd.append('conversation[destination_url]', payload.destination_url)
        }

        // Files: `assets[]` is the catch-all the controller's multipart
        // handler consumes; the typed `conversation[reference_*][]` keys let
        // Rails strong-params slot each file into the right bucket so the
        // response's `input_reference_data.{image,video,audio}_data` arrays
        // come back populated correctly.
        //
        // MIME-based routing mirrors the sender in Home.tsx — image/* →
        // reference_images, video/* → reference_videos, audio/* →
        // reference_audio, anything else stays on `assets[]` only.
        // No empty-string placeholders for missing buckets — Rails rejects
        // those when the param is expected to be a hash.
        payload.reference_images!.forEach((file) => {
          fd.append('assets[]', file, file.name)
          const type = (file.type || '').toLowerCase()
          if (type.startsWith('image/')) {
            fd.append('conversation[reference_images][]', file, file.name)
          } else if (type.startsWith('video/')) {
            fd.append('conversation[reference_videos][]', file, file.name)
          } else if (type.startsWith('audio/')) {
            fd.append('conversation[reference_audio][]', file, file.name)
          }
        })

        await chatSendMessage(queryMsg, { workflow: 'ai_creative_generation' }, fd)
      } else {
        const extraPayload: Record<string, unknown> = {
          workflow: 'ai_creative_generation',
          content_size: payload.content_size,
          input_reference_data: {
            input_image_data: {},
            input_audio_data: {},
            input_video_data: {},
          },
        }
        if (!conversationId && campaignId) {
          extraPayload.campaign_id = parseInt(campaignId, 10)
        }
        if (payload.brand_kit_id) {
          extraPayload.brand_kit_id = payload.brand_kit_id
        }
        if (payload.destination_url) {
          extraPayload.destination_url = payload.destination_url
        }

        await chatSendMessage(queryMsg, extraPayload)
      }
    } catch (error) {
      setIsCreatingAdUnits(false)
      toast.error('Failed to generate creative. Please try again.')
    }
  }

  return (
    <>
      <Helmet>
        <title>{campaignData?.objective ? `Campaign: ${campaignData.objective}` : 'Campaign'} - AgenX</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header
          variant="campaigns"
          contextChip={
            campaignData?.objective
              ? {
                  icon: <LayoutDashboard className="h-3 w-3" />,
                  title: campaignData.objective,
                  subtitle: 'Campaign Strategy',
                }
              : null
          }
        />

        <main className="flex-1 bg-white dark:bg-gray-950 pt-14 sm:pt-16 transition-colors">
          <div className="h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)] overflow-hidden">
            {/* Loading State */}
            {isLoadingCampaign && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-slate-400">Loading campaign data...</p>
                </div>
              </div>
            )}
            
            {/* Results Panel - Merged from ResultsPanel component */}
            {!isLoadingCampaign && (
            <div className="flex h-full">
              {/* Chat Panel - Left side, extends to touch header. Width is
                  user-resizable via the drag handle on its right edge while
                  expanded; collapse keeps a fixed icon-rail width. */}
              {handleChatMessage && (
                <div
                  ref={chatPanelRef}
                  style={isChatPanelCollapsed ? undefined : { width: `${chatPanelWidth}px` }}
                  className={`${isChatPanelCollapsed ? 'w-16' : ''} bg-white/95 dark:bg-slate-900/80 backdrop-blur border-r border-slate-200/70 dark:border-slate-800/80 flex-shrink-0 flex flex-col h-full ${isResizingChat ? '' : 'transition-[width] duration-300'} relative`}
                >
                  {/* Collapse/Expand Button */}
                  <button
                    onClick={() => setIsChatPanelCollapsed(!isChatPanelCollapsed)}
                    className="absolute -right-3 top-4 z-30 w-6 h-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
                    title={isChatPanelCollapsed ? 'Expand chat panel' : 'Collapse chat panel'}
                  >
                    {isChatPanelCollapsed ? (
                      <PanelLeft className="h-3.5 w-3.5" />
                    ) : (
                      <PanelLeftClose className="h-3.5 w-3.5" />
                    )}
                  </button>

                  {/* Resize handle — thin invisible strip on the right edge.
                      Highlights on hover/active. Hidden while collapsed. */}
                  {!isChatPanelCollapsed && (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize chat panel"
                      onMouseDown={handleChatPanelResizeStart}
                      onDoubleClick={handleChatPanelResizeDoubleClick}
                      title="Drag to resize · double-click to reset"
                      className="group absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 -mr-px"
                    >
                      <div
                        className={`h-full w-px ml-auto transition-colors ${
                          isResizingChat
                            ? 'w-0.5 bg-primary-500'
                            : 'bg-transparent group-hover:w-0.5 group-hover:bg-primary-400/70'
                        }`}
                      />
                    </div>
                  )}

                  
                  {/* Chat Panel Content */}
                  {isChatPanelCollapsed ? (
                    <div className="flex flex-col items-center pt-4 text-slate-400 h-full">
                      {/* Chat Icon */}
                      <button
                        onClick={() => setIsChatPanelCollapsed(false)}
                        className="p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors mb-4"
                        title="Expand chat panel"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </button>
                      
                      {/* Divider */}
                      <div className="w-8 border-t border-slate-200 dark:border-slate-700 mb-4"></div>
                      
                      {/* Tab Navigation Icons */}
                      <nav className="flex flex-col items-center space-y-2">
                        {tabs.map((tab) => {
                          const Icon = tab.icon
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id)}
                              data-tab-id={tab.id}
                              className={`p-2.5 rounded-lg transition-colors duration-200 ${
                                activeTab === tab.id || (tab.id === 'creative' && activeTab === 'creativeStudio')
                                  ? 'bg-primary-500 text-white'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
                              }`}
                              title={tab.label}
                            >
                              <Icon className="h-5 w-5" />
                            </button>
                          )
                        })}
                      </nav>
                    </div>
                  ) : (
                    <ChatPanel 
                      messages={chatMessages}
                      onSendMessage={handleChatMessage}
                      isLoading={isChatLoading}
                      currentInput={currentChatInput}
                      activeAgent={showAICreativeModal || isCreatingAdUnits ? 'AI Creative Generation' : null}
                    />
                  )}
                </div>
              )}

              {/* Main Content Area - Right side. The old "Campaign Strategy"
                  header row was removed in favor of the compact context chip
                  rendered next to the logo in the global Header. */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Tab Navigation and Content */}
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                  {/* Fixed Tab Navigation - Hidden when chat panel is collapsed or in Creative Studio */}
                  {!isChatPanelCollapsed && activeTab !== 'creativeStudio' && (
                    <div className="w-full lg:w-64 xl:w-72 2xl:w-64 bg-white dark:bg-gray-950 border-b lg:border-b-0 lg:border-r border-gray-200/30 dark:border-slate-800/50 flex-shrink-0">
                      <nav className="p-2 flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-1 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto lg:h-full">
                        {tabs.map((tab) => {
                          const Icon = tab.icon
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id)}
                              data-tab-id={tab.id}
                              className={`flex-shrink-0 text-left px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                                activeTab === tab.id || (tab.id === 'creative' && activeTab === 'creativeStudio')
                                  ? 'bg-primary-500 text-white'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden md:inline">{tab.label}</span>
                                <span className="md:hidden">{tab.label.split(' ')[0]}</span>
                              </div>
                            </button>
                          )
                        })}
                      </nav>
                    </div>
                  )}

                  {/* Scrollable Tab Content */}
                  <div className="flex-1 overflow-hidden min-w-0 flex flex-col bg-white dark:bg-gray-950">
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </main>

        {/* Campaign Form Modal */}
        <CampaignFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleGenerateStrategy}
          isLoading={false}
          currentData={currentFormData || campaignData}
        />

        {/* AI Creative Generation Modal */}
        <AICreativeModal
          isOpen={showAICreativeModal}
          onClose={() => setShowAICreativeModal(false)}
          onGenerate={handleAICreativeGenerate}
          isGenerating={isCreatingAdUnits}
        />

        {/* Optional assets upload before create-ad-units */}
        <CreateAdUnitsAssetsModal
          isOpen={isAssetsModalOpen}
          onClose={handleAssetsModalSkip}
          onSubmit={handleAssetsModalSubmit}
          isSubmitting={isCreatingAdUnits}
        />

      </div>
    </>
  )
}
